import React from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Button } from "../../../components/ui/button"
import { Input } from "../../../components/ui/input"
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../../../components/ui/select"
import { ChevronRight, DollarSign, Bitcoin, Briefcase, LineChart, PieChart, X } from 'lucide-react'
import { useEditInvestments } from '../hooks/EditInvestments'
import { Card, CardHeader, CardTitle, CardContent } from "../../../components/ui/card"
import AnimatedBackground from '../../utils/AnimatedBackground'
import TopBar from '../../utils/TopBar';
import Footer from '../../utils/Footer';

const EditInvestments: React.FC = () => {
  const {
    investments,
    cryptoInvestments,
    tesouroDiretoInvestments,
    countries,
    negotiationMarkets,
    selectedType,
    handleInputChange,
    handleCryptoInputChange,
    handleTesouroDiretoInputChange,
    handleSubmit,
    handleAssetTypeSelection,
    availableTypes,
    handleDelete,
  } = useEditInvestments()

  const navigate = useNavigate()

  const assetTypes = [
    { type: 'stock', icon: DollarSign, label: 'Stocks' },
    { type: 'crypto', icon: Bitcoin, label: 'Crypto Currency' },
    { type: 'tesouro-direto', icon: Briefcase, label: 'Tesouro Direto' },
    { type: 'fixed-investment', icon: LineChart, label: 'Fixed Investments' },
    { type: 'investment-funds', icon: PieChart, label: 'Investment Funds' },
  ].filter(type => {
    if (type.type === 'crypto') return availableTypes['crypto']
    return availableTypes[type.type]
  })

  const renderInvestmentTable = () => {
    switch (selectedType) {
      case 'stock':
        return (
          <table className="min-w-full divide-y divide-gray-700">
            <thead>
              <tr>
                <th className="w-12 px-2 py-3 text-left text-xs font-medium text-gray-300 uppercase"></th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">Operation</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">Country</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">Market</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">Currency</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">Ticker</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">Quantity</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">Price</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {investments.map((investment, index) => (
                <tr key={investment.id}>
                  <td className="w-12 px-2 py-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(investment.id)}
                      className="hover:bg-red-500/20"
                    >
                      <X className="h-4 w-4 text-red-500" />
                    </Button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <Select 
                      value={investment.operation_type} 
                      onValueChange={(value) => handleInputChange(index, 'operation_type', value)}
                    >
                      <SelectTrigger className="bg-gray-700 text-gray-300 border-0">
                        <SelectValue placeholder="Select operation" />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-700 text-gray-300">
                        <SelectItem value="buy">Buy</SelectItem>
                        <SelectItem value="sell">Sell</SelectItem>
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <Select 
                      value={investment.country} 
                      onValueChange={(value) => handleInputChange(index, 'country', value)}
                    >
                      <SelectTrigger className="bg-gray-700 text-gray-300 border-0">
                        <SelectValue placeholder="Select country" />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-700 text-gray-300">
                        {countries.map(country => (
                          <SelectItem key={country} value={country}>{country}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <Select 
                      value={investment.negotiation_market} 
                      onValueChange={(value) => handleInputChange(index, 'negotiation_market', value)}
                    >
                      <SelectTrigger className="bg-gray-700 text-gray-300 border-0">
                        <SelectValue placeholder="Select market" />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-700 text-gray-300">
                        {negotiationMarkets[investment.country].map((market: string) => (
                          <SelectItem key={market} value={market}>{market}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <Input
                      value={investment.currency}
                      onChange={(e) => handleInputChange(index, 'currency', e.target.value)}
                      className="bg-gray-700 text-gray-300 border-0"
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <Input
                      value={investment.ticker}
                      onChange={(e) => handleInputChange(index, 'ticker', e.target.value)}
                      className="bg-gray-700 text-gray-300 border-0"
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <Input
                      type="number"
                      value={investment.quantity}
                      onChange={(e) => handleInputChange(index, 'quantity', parseFloat(e.target.value))}
                      className="bg-gray-700 text-gray-300 border-0"
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <Input
                      type="number"
                      value={investment.price}
                      onChange={(e) => handleInputChange(index, 'price', parseFloat(e.target.value))}
                      className="bg-gray-700 text-gray-300 border-0"
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <Input
                      type="date"
                      value={investment.operation_date}
                      onChange={(e) => handleInputChange(index, 'operation_date', e.target.value)}
                      className="bg-gray-700 text-gray-300 border-0"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )

      case 'crypto':
        return (
          <table className="min-w-full divide-y divide-gray-700">
            <thead>
              <tr>
                <th className="w-12 px-2 py-3 text-left text-xs font-medium text-gray-300 uppercase"></th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">Operation</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">Currency</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">Ticker</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">Quantity</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">Price</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">Operation Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {cryptoInvestments.map((investment, index) => (
                <tr key={investment.id} className="hover:bg-gray-700/30">
                  <td className="w-12 px-2 py-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(investment.id)}
                      className="hover:bg-red-500/20"
                    >
                      <X className="h-4 w-4 text-red-500" />
                    </Button>
                  </td>
                  <td className="px-4 py-2">
                    <Select value={investment.operation_type} onValueChange={(value) => handleCryptoInputChange(index, 'operation_type', value)}>
                      <SelectTrigger className="w-32 bg-gray-700/50 text-gray-300 border-0">
                        <SelectValue placeholder="Select operation" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="buy">Buy</SelectItem>
                        <SelectItem value="sell">Sell</SelectItem>
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-4 py-2">
                    <Input
                      value={investment.currency}
                      onChange={(e) => handleCryptoInputChange(index, 'currency', e.target.value)}
                      className="bg-gray-700/50 text-gray-300 border-0"
                    />
                  </td>
                  <td className="px-4 py-2">
                    <Input
                      value={investment.ticker}
                      onChange={(e) => handleCryptoInputChange(index, 'ticker', e.target.value)}
                      className="bg-gray-700/50 text-gray-300 border-0"
                    />
                  </td>
                  <td className="px-4 py-2">
                    <Input
                      type="number"
                      value={investment.quantity}
                      onChange={(e) => handleCryptoInputChange(index, 'quantity', parseFloat(e.target.value))}
                      className="bg-gray-700/50 text-gray-300 border-0"
                    />
                  </td>
                  <td className="px-4 py-2">
                    <Input
                      type="number" 
                      value={investment.price}
                      onChange={(e) => handleCryptoInputChange(index, 'price', parseFloat(e.target.value))}
                      className="bg-gray-700/50 text-gray-300 border-0"
                    />
                  </td>
                  <td className="px-4 py-2">
                    <Input
                      type="date"
                      value={investment.operation_date}
                      onChange={(e) => handleCryptoInputChange(index, 'operation_date', e.target.value)}
                      className="bg-gray-700/50 text-gray-300 border-0"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )

      case 'tesouro-direto':
        return (
          <table className="min-w-full divide-y divide-gray-700">
            <thead>
              <tr>
                <th className="w-12 px-2 py-3 text-left text-xs font-medium text-gray-300 uppercase"></th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">Bond Type</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">Operation</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">Interest</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">Rate</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">Operation Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">End Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">Quantity</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">Operation Amount</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {tesouroDiretoInvestments.map((investment, index) => (
                <tr key={investment.id} className="hover:bg-gray-700/30">
                  <td className="w-12 px-2 py-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(investment.id)}
                      className="hover:bg-red-500/20"
                    >
                      <X className="h-4 w-4 text-red-500" />
                    </Button>
                  </td>
                  <td className="px-4 py-2">
                    <Input
                      value={investment.bond_type}
                      onChange={(e) => handleTesouroDiretoInputChange(index, 'bond_type', e.target.value)}
                      className="bg-gray-700/50 text-gray-300 border-0"
                    />
                  </td>
                  <td className="px-4 py-2">
                    <Select value={investment.operation_type} onValueChange={(value) => handleTesouroDiretoInputChange(index, 'operation_type', value)}>
                      <SelectTrigger className="w-32 bg-gray-700/50 text-gray-300 border-0">
                        <SelectValue placeholder="Select operation" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="buy">Buy</SelectItem>
                        <SelectItem value="sell">Sell</SelectItem>
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-4 py-2">
                    <Input
                      type="checkbox"
                      checked={investment.interest_semianual}
                      onChange={(e) => handleTesouroDiretoInputChange(index, 'interest_semianual', e.target.checked)}
                      className="bg-gray-700/50 text-gray-300 border-0"
                    />
                  </td>
                  <td className="px-4 py-2">
                    <Input
                      type="number"
                      value={investment.rate}
                      onChange={(e) => handleTesouroDiretoInputChange(index, 'rate', parseFloat(e.target.value))}
                      className="bg-gray-700/50 text-gray-300 border-0"
                    />
                  </td>
                  <td className="px-4 py-2">
                    <Input
                      type="date"
                      value={investment.operation_date}
                      onChange={(e) => handleTesouroDiretoInputChange(index, 'operation_date', e.target.value)}
                      className="bg-gray-700/50 text-gray-300 border-0"
                    />
                  </td>
                  <td className="px-4 py-2">
                    <Input
                      type="date"
                      value={investment.end_date}
                      onChange={(e) => handleTesouroDiretoInputChange(index, 'end_date', e.target.value)}
                      className="bg-gray-700/50 text-gray-300 border-0"
                    />
                  </td>
                  <td className="px-4 py-2">
                    <Input
                      type="number"
                      value={investment.quantity}
                      onChange={(e) => handleTesouroDiretoInputChange(index, 'quantity', parseFloat(e.target.value))}
                      className="bg-gray-700/50 text-gray-300 border-0"
                    />
                  </td>
                  <td className="px-4 py-2">
                    <Input
                      type="number"
                      value={investment.operation_amount}
                      onChange={(e) => handleTesouroDiretoInputChange(index, 'operation_amount', parseFloat(e.target.value))}
                      className="bg-gray-700/50 text-gray-300 border-0"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )

      default:
        return null
    }
  }

  if (!selectedType) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex flex-col relative">
        <AnimatedBackground />
        <div className="relative z-10 flex flex-col min-h-screen">
          <TopBar 
            title="Edit Investments"
            subtitle="Edit your investments data"
          />
          <main className="relative z-10 p-4 sm:p-6 lg:p-8 flex items-center justify-center flex-1">
            <div className="w-full max-w-3xl">
              <Card className="bg-[#0f1115] border-gray-800">
                <CardHeader>
                  <CardTitle className="text-white text-center">Select Asset Type</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 gap-6">
                    {assetTypes.map(({ type, icon: Icon, label }) => (
                      <motion.button
                        key={type}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => handleAssetTypeSelection(type)}
                        className="relative flex items-center justify-between px-8 py-6 rounded-xl transition-all bg-black text-white shadow-lg hover:shadow-xl group overflow-hidden w-full"
                      >
                        <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 via-purple-500/20 to-indigo-500/20 opacity-0 group-hover:opacity-100 group-active:opacity-100 transition-opacity"></div>
                        <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 via-purple-500/20 to-indigo-500/20 animate-gradient-x"></div>
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-purple-400/10 to-transparent animate-pulse"></div>
                        <div className="relative flex items-center z-10">
                          <Icon className="w-8 h-8 mr-4" />
                          <span className="text-xl font-semibold">{label}</span>
                        </div>
                        <ChevronRight className="relative w-6 h-6 transform group-hover:translate-x-2 transition-transform z-10" />
                      </motion.button>
                    ))}
                  </div>
                  <div className="mt-6 flex justify-center">
                    <Button
                      onClick={() => navigate('/investments')}
                      className="bg-red-600 hover:bg-red-700 text-white"
                    >
                      Back
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </main>

          <footer className="bg-black mt-auto border-t border-gray-800">
            <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8 text-center text-gray-400 text-sm">
              <p>&copy; 2024 MyMoney Analyst. All rights reserved.</p>
            </div>
          </footer>
        </div>
        <style>{`
          @keyframes gradient-x {
            0% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
            100% { background-position: 0% 50%; }
          }
          .animate-gradient-x {
            animation: gradient-x 3s ease infinite;
            background-size: 400% 400%;
          }
          @keyframes pulse {
            0%, 100% { opacity: 0.1; }
            50% { opacity: 0.4; }
          }
          .animate-pulse {
            animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
          }
        `}</style>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex flex-col relative">
      <AnimatedBackground />
      <div className="relative z-10 flex flex-col min-h-screen">
        <TopBar 
          title="Edit Investments"
          subtitle="Edit your investments data"
        />

        <main className="relative z-10 p-4 sm:p-6 lg:p-8">
          <div className="max-w-7xl mx-auto">
            <Card className="bg-[#0f1115] border-gray-800">
              <CardHeader>
                <CardTitle className="text-white">Investment Details</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit}>
                  <div className="overflow-x-auto">
                    {renderInvestmentTable()}
                  </div>
                  <div className="mt-6 flex justify-between">
                    <Button 
                      type="button"
                      onClick={() => {
                        handleAssetTypeSelection('');
                        navigate('/investments/edit');
                      }}
                      className="bg-red-600 hover:bg-red-700 text-white"
                    >
                      Back
                    </Button>
                    <Button 
                      type="submit"
                      className="bg-green-600 hover:bg-green-700 text-white"
                    >
                      Save Changes
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        </main>
        <Footer />
      </div>
    </div>
  )
}

export default EditInvestments